import i18next from "i18next";
import type { Permission } from "@/types";

const modules: Record<string, string> = {
  dentistry: "Odontologia", orthodontics: "Ortodontia", anamnese: "Anamnese",
  employee: "Profissionais", exam: "Exames", "finance account": "Contas financeiras",
  "finance entry": "Lançamentos financeiros", frequency: "Frequências", group: "Grupos",
  medication: "Medicamentos", patient: "Pacientes", permission: "Permissões",
  procedure: "Procedimentos", user: "Usuários", providers: "Fornecedores", role: "Perfis de acesso",
  scheduling: "Agenda", report: "Relatórios", voucher: "Vales", budget: "Orçamentos",
  positions: "Cargos", convenants: "Convênios", models: "Modelos de contrato",
  modelstypes: "Tipos de contrato", category: "Categorias financeiras", crm: "Relacionamento com pacientes",
  "stock category": "Categorias de estoque", "stock item": "Itens de estoque", "stock movement": "Movimentações de estoque",
};
const actions: Record<string, string> = {
  read: "Consultar", view: "Consultar", write: "Criar e editar", create: "Criar", update: "Editar", edit: "Editar",
  delete: "Excluir", export: "Exportar", finalize: "Finalizar registros", operations: "Operar acompanhamento",
  enable: "Ativar", disable: "Desativar", pay: "Pagar", finished: "Finalizar", canceled: "Cancelar", confirmed: "Confirmar",
};
const specificLabels: Record<string, string> = {
  "view scheduling reports": "Consultar relatórios de agenda", "view patient reports": "Consultar relatórios de pacientes",
  "view cash flow": "Consultar fluxo de caixa", "view budget details": "Consultar detalhes de orçamentos",
  "view budget reports": "Consultar relatórios de orçamentos", "set budget status": "Alterar situação de orçamentos",
  "approve stock movement": "Aprovar movimentações de estoque",
};
export function roleLabel(name: string) {
  return ({ admin: "Administrador", receptionist: "Recepção", secretary: "Secretaria", accountant: "Financeiro" } as Record<string, string>)[name] ?? name;
}
function translatedLabel(value: string) {
  const key = `LABEL.${value.toLowerCase()}`;
  if (!i18next.exists(key)) return undefined;
  return String(i18next.t(key));
}
function readable(value: string) { return value.replace(/[._-]/g, " "); }
export function isTenantGrant(permission: Permission & { guard_name?: string }) {
  return (!permission.guard_name || permission.guard_name === "tenant") && !["modules.manage", "system.manage"].includes(permission.name);
}
export function moduleLabel(module: string) { return modules[module] ?? translatedLabel(module) ?? `Módulo: ${readable(module)}`; }
export function permissionAction(permission: Permission) {
  return permission.name.includes(".") ? permission.name.split(".").at(-1)! : permission.name.split(" ")[0];
}
export function permissionLabel(permission: Permission) {
  return specificLabels[permission.name] ?? actions[permissionAction(permission)] ?? translatedLabel(permission.name) ?? `Ação: ${readable(permission.name)}`;
}
export function permissionGroups(permissions: Permission[], search = "") {
  const normalize = (value: string) => value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const grouped = new Map<string, Permission[]>();
  for (const permission of permissions) {
    // The API owns guard filtering. System grants must never become a convenience choice.
    if (!isTenantGrant(permission)) continue;
    const group = grouped.get(permission.module) ?? [];
    group.push(permission);
    grouped.set(permission.module, group);
  }
  return [...grouped.entries()].filter(([module, grants]) => normalize(
    `${moduleLabel(module)} ${module} ${module === "dentistry" ? "dental dentes odontograma" : ""} ${grants.map(permissionLabel).join(" ")}`,
  ).includes(normalize(search.trim()))).sort(([a], [b]) => {
    const priority = (module: string) => module === "dentistry" ? 0 : module === "orthodontics" ? 1 : 2;
    return priority(a) - priority(b) || moduleLabel(a).localeCompare(moduleLabel(b), "pt-BR");
  });
}
export function addBasicGrants(selected: string[], permissions: Permission[], level: "read" | "edit") {
  const allowed = level === "read" ? ["read", "view"] : ["read", "view", "write", "create", "update", "edit"];
  return [...new Set([...selected, ...permissions.filter(permission => allowed.includes(permissionAction(permission))).map(permission => permission.id)])];
}
